import React, { useState, useEffect, useCallback, useRef } from 'react';
import { X, Package, Plus, Minus, Building2, Store, MapPin, Calculator, Camera, Box, AlertCircle, Lock, RefreshCw, CheckCircle2, Loader2, Tag } from 'lucide-react';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { CategoryAutocomplete } from '../ui/CategoryAutocomplete';
import { ItemImageUpload } from '../ui/ItemImageUpload';
import { BarcodeScanner } from '../ui/BarcodeScanner';
import { LocationPicker, type BinDetails } from '../warehouse/LocationPicker';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import { resolveTenantAdminId } from '../../lib/tenantStorage';
import { useInventory } from '../../contexts/InventoryContext';
import { supabase } from '../../lib/supabase';
import {
  ensureDefaultLocations,
  fetchLocationQuantitiesForProduct,
  saveLocationQuantities,
  type LocationQuantity
} from '../../utils/locationInventory';
import {
  assignProductToBin,
  transferProductBetweenBins,
  removeProductFromBin,
  getProductBinLocation
} from '../../lib/services/binInventoryService';
import type { ProductBinLocation } from '../../types/warehouse';

interface ProductColor {
  id: string;
  name: string;
  hex_code?: string;
}

interface ProductSize {
  id: string;
  name: string;
  sort_order: number;
}

interface ProductLocation {
  id: string;
  name: string;
}

interface LocationEntry {
  locationId: string;
  locationName: string;
  quantity: number;
}

interface SiblingVariant {
  id: string;
  name: string;
  color?: string;
  size?: string;
}

interface FormData {
  name: string;
  category: string;
  location: string;
  bin_location: string;
  batch: string;
  reference_code: string;
  buy_price: string;
  sell_price: string;
  danger_category: string;
  image_url: string | undefined;
  color_id: string;
  size_id: string;
  weight: string;
  weight_unit: string;
}

interface AddItemFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: any) => Promise<boolean | string>;
  editingItem?: any;
  colors: ProductColor[];
  sizes: ProductSize[];
  locations: ProductLocation[];
  categories: string[];
  adminId: string;
  hasPricingAccess: boolean;
  isAdmin: boolean;
  onBarcodeScanned?: (barcode: string) => void;
  onLocationQuantitiesChanged?: () => void;
  onBinAssignmentChanged?: () => void;
}

const getLocationIcon = (locationName: string) => {
  const name = locationName.toLowerCase();
  if (name === 'warehouse' || name.includes('warehouse')) {
    return Building2;
  }
  if (name === 'shop' || name.includes('shop') || name.includes('store')) {
    return Store;
  }
  return MapPin;
};

const generateNewSKU = () => {
  const now = new Date();
  const year = now.getFullYear().toString().slice(-2);
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  const random = Math.floor(Math.random() * 9999).toString().padStart(4, '0');
  return `SKU${year}${month}${day}-${random}`;
};

export const AddItemForm: React.FC<AddItemFormProps> = ({
  isOpen,
  onClose,
  onSubmit,
  editingItem,
  colors,
  sizes,
  locations,
  categories,
  adminId,
  hasPricingAccess,
  isAdmin,
  onBarcodeScanned,
  onLocationQuantitiesChanged,
  onBinAssignmentChanged
}) => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const tenantAdminId = resolveTenantAdminId(user, null);
  const { refreshInventory } = useInventory();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showScanner, setShowScanner] = useState(false);
  const [availableLocations, setAvailableLocations] = useState<ProductLocation[]>([]);
  const [selectedBinDetails, setSelectedBinDetails] = useState<BinDetails | null>(null);
  const [existingBinLocation, setExistingBinLocation] = useState<ProductBinLocation | null>(null);
  const [binAssignmentError, setBinAssignmentError] = useState<string | null>(null);

  const [skuValue, setSkuValue] = useState('');
  const [originalSku, setOriginalSku] = useState('');
  const [skuValidating, setSkuValidating] = useState(false);
  const [skuConflict, setSkuConflict] = useState<{ productName: string } | null>(null);
  const [skuAvailable, setSkuAvailable] = useState(false);
  const [showBatchModal, setShowBatchModal] = useState(false);
  const [siblingVariants, setSiblingVariants] = useState<SiblingVariant[]>([]);
  const [pendingItemData, setPendingItemData] = useState<any>(null);
  const skuDebounceRef = useRef<NodeJS.Timeout | null>(null);

  const [formData, setFormData] = useState<FormData>({
    name: '',
    category: '',
    location: '',
    bin_location: '',
    batch: '',
    reference_code: '',
    buy_price: '',
    sell_price: '',
    danger_category: '',
    image_url: undefined,
    color_id: '',
    size_id: '',
    weight: '',
    weight_unit: 'g'
  });

  const [locationQuantities, setLocationQuantities] = useState<LocationEntry[]>([]);

  const initializeLocations = useCallback(async () => {
    if (!adminId) return;

    const defaultLocs = await ensureDefaultLocations(adminId);
    setAvailableLocations(defaultLocs);

    if (editingItem) {
      const existingQuantities = await fetchLocationQuantitiesForProduct(editingItem.id, adminId);

      if (existingQuantities.length > 0) {
        setLocationQuantities(existingQuantities.map(q => ({
          locationId: q.locationId,
          locationName: q.locationName,
          quantity: q.quantity
        })));
      } else {
        const warehouse = defaultLocs.find(l => l.name.toLowerCase() === 'warehouse');
        const shop = defaultLocs.find(l => l.name.toLowerCase() === 'shop');

        const entries: LocationEntry[] = [];
        if (warehouse) {
          entries.push({
            locationId: warehouse.id,
            locationName: warehouse.name,
            quantity: editingItem.quantity || 0
          });
        }
        if (shop) {
          entries.push({
            locationId: shop.id,
            locationName: shop.name,
            quantity: 0
          });
        }
        setLocationQuantities(entries);
      }
    } else {
      const warehouse = defaultLocs.find(l => l.name.toLowerCase() === 'warehouse');
      const shop = defaultLocs.find(l => l.name.toLowerCase() === 'shop');

      const entries: LocationEntry[] = [];
      if (warehouse) {
        entries.push({
          locationId: warehouse.id,
          locationName: warehouse.name,
          quantity: 0
        });
      }
      if (shop) {
        entries.push({
          locationId: shop.id,
          locationName: shop.name,
          quantity: 0
        });
      }
      setLocationQuantities(entries);
    }
  }, [adminId, editingItem]);

  useEffect(() => {
    if (isOpen) {
      initializeLocations();
    }
  }, [isOpen, initializeLocations]);

  useEffect(() => {
    const initializeForm = async () => {
      setSelectedBinDetails(null);
      setExistingBinLocation(null);
      setBinAssignmentError(null);
      setSkuConflict(null);
      setSkuAvailable(false);
      setShowBatchModal(false);
      setSiblingVariants([]);
      setPendingItemData(null);

      if (editingItem && tenantAdminId) {
        const binLoc = await getProductBinLocation(editingItem.id, tenantAdminId);
        setExistingBinLocation(binLoc);

        const binLocationDisplay = binLoc ? binLoc.full_address : '';
        const currentSku = editingItem.sku || '';

        setSkuValue(currentSku);
        setOriginalSku(currentSku);

        setFormData({
          name: editingItem.name || '',
          category: editingItem.category || '',
          location: editingItem.location || '',
          bin_location: binLocationDisplay,
          batch: editingItem.batch || '',
          reference_code: editingItem.reference_code || '',
          buy_price: editingItem.buy_price?.toString() || '',
          sell_price: editingItem.sell_price?.toString() || '',
          danger_category: editingItem.danger_category || '',
          image_url: editingItem.image_url,
          color_id: editingItem.color_id || '',
          size_id: editingItem.size_id || '',
          weight: editingItem.weight?.toString() || '',
          weight_unit: editingItem.weight_unit || 'g'
        });

        if (binLoc) {
          setSelectedBinDetails({
            warehouseId: binLoc.warehouse_id,
            warehouseName: binLoc.warehouse_name,
            zoneId: binLoc.zone_id || undefined,
            zoneCode: binLoc.zone_code || undefined,
            rackId: binLoc.rack_id,
            rackCode: binLoc.rack_code,
            binId: binLoc.bin_id,
            binCode: binLoc.bin_code,
            fullPath: binLoc.full_address
          });
        }
      } else {
        setSkuValue('');
        setOriginalSku('');
        setFormData({
          name: '',
          category: '',
          location: '',
          bin_location: '',
          batch: '',
          reference_code: '',
          buy_price: '',
          sell_price: '',
          danger_category: '',
          image_url: undefined,
          color_id: '',
          size_id: '',
          weight: '',
          weight_unit: 'g'
        });
      }
    };

    if (isOpen) {
      initializeForm();
    }
  }, [editingItem, isOpen, tenantAdminId]);

  useEffect(() => {
    if (!isAdmin || !editingItem || !skuValue.trim()) {
      setSkuConflict(null);
      setSkuAvailable(false);
      return;
    }

    if (skuValue.trim() === originalSku) {
      setSkuConflict(null);
      setSkuAvailable(false);
      return;
    }

    if (skuDebounceRef.current) clearTimeout(skuDebounceRef.current);

    setSkuValidating(true);
    setSkuConflict(null);
    setSkuAvailable(false);

    skuDebounceRef.current = setTimeout(async () => {
      try {
        const { data } = await supabase
          .from('products')
          .select('name')
          .eq('admin_id', adminId)
          .eq('sku', skuValue.trim())
          .neq('id', editingItem.id)
          .maybeSingle();

        if (data) {
          setSkuConflict({ productName: data.name });
          setSkuAvailable(false);
        } else {
          setSkuConflict(null);
          setSkuAvailable(true);
        }
      } catch {
        setSkuConflict(null);
        setSkuAvailable(false);
      } finally {
        setSkuValidating(false);
      }
    }, 300);

    return () => {
      if (skuDebounceRef.current) clearTimeout(skuDebounceRef.current);
    };
  }, [skuValue, originalSku, isAdmin, editingItem, adminId]);

  const totalQuantity = locationQuantities.reduce((sum, entry) => sum + entry.quantity, 0);

  const handleQuantityChange = (locationId: string, delta: number) => {
    setLocationQuantities(prev =>
      prev.map(entry =>
        entry.locationId === locationId
          ? { ...entry, quantity: Math.max(0, entry.quantity + delta) }
          : entry
      )
    );
  };

  const handleQuantityInput = (locationId: string, value: string) => {
    const numValue = parseInt(value) || 0;
    setLocationQuantities(prev =>
      prev.map(entry =>
        entry.locationId === locationId
          ? { ...entry, quantity: Math.max(0, numValue) }
          : entry
      )
    );
  };

  const addLocationRow = () => {
    const usedLocationIds = new Set(locationQuantities.map(e => e.locationId));
    const unusedLocation = availableLocations.find(l => !usedLocationIds.has(l.id));

    if (unusedLocation) {
      setLocationQuantities(prev => [
        ...prev,
        {
          locationId: unusedLocation.id,
          locationName: unusedLocation.name,
          quantity: 0
        }
      ]);
    }
  };

  const removeLocationRow = (locationId: string) => {
    if (locationQuantities.length <= 1) return;
    setLocationQuantities(prev => prev.filter(e => e.locationId !== locationId));
  };

  const changeLocation = (oldLocationId: string, newLocationId: string) => {
    const newLocation = availableLocations.find(l => l.id === newLocationId);
    if (!newLocation) return;

    setLocationQuantities(prev =>
      prev.map(entry =>
        entry.locationId === oldLocationId
          ? { ...entry, locationId: newLocationId, locationName: newLocation.name }
          : entry
      )
    );
  };

  const executeSubmit = async (itemData: any, doBulk: boolean) => {
    setIsSubmitting(true);
    setBinAssignmentError(null);
    setShowBatchModal(false);

    try {
      const result = await onSubmit(itemData);

      if (result) {
        const productId = editingItem ? editingItem.id : (typeof result === 'string' ? result : null);

        if (productId) {
          await saveLocationQuantities(
            productId,
            adminId,
            locationQuantities.map(e => ({
              locationId: e.locationId,
              quantity: e.quantity
            }))
          );

          if (onLocationQuantitiesChanged) {
            onLocationQuantitiesChanged();
          }

          const userId = user?.id || adminId;
          let binChanged = false;

          if (selectedBinDetails) {
            const newBinId = selectedBinDetails.binId;
            const hadExistingBin = existingBinLocation?.bin_id;
            const sameBin = hadExistingBin === newBinId;

            if (!sameBin) {
              if (hadExistingBin && existingBinLocation?.bin_inventory_id) {
                const transferResult = await transferProductBetweenBins(
                  {
                    product_id: productId,
                    from_bin_id: hadExistingBin,
                    to_bin_id: newBinId,
                    warehouse_id: selectedBinDetails.warehouseId,
                    quantity: totalQuantity,
                    reason: 'Bin location changed via item edit'
                  },
                  userId
                );

                if (!transferResult.success) {
                  setBinAssignmentError(transferResult.error || 'Failed to transfer product to new bin');
                  return;
                }
                binChanged = true;
              } else {
                const assignResult = await assignProductToBin(
                  {
                    bin_id: newBinId,
                    product_id: productId,
                    warehouse_id: selectedBinDetails.warehouseId,
                    quantity: totalQuantity
                  },
                  adminId,
                  userId
                );

                if (!assignResult.success) {
                  if (assignResult.error?.includes('already assigned')) {
                    setBinAssignmentError(
                      `This product is already assigned to a bin. Use the warehouse map to transfer it to a different location.`
                    );
                  } else {
                    setBinAssignmentError(assignResult.error || 'Failed to assign product to bin');
                  }
                  return;
                }
                binChanged = true;
              }
            }
          } else if (existingBinLocation?.bin_inventory_id && !selectedBinDetails) {
            await removeProductFromBin(existingBinLocation.bin_inventory_id);
            binChanged = true;
          }

          if (binChanged && onBinAssignmentChanged) {
            onBinAssignmentChanged();
          }

          const skuChanged = isAdmin && editingItem && itemData.sku && itemData.sku !== originalSku;

          if (skuChanged) {
            try {
              if (!doBulk) {
                await supabase.from('activity_logs').insert({
                  admin_id: adminId,
                  user_id: userId,
                  action_type: 'sku_changed',
                  entity_type: 'product',
                  entity_id: productId,
                  description: `SKU changed for "${editingItem.name}": ${originalSku || 'none'} → ${itemData.sku}`,
                  metadata: {
                    old_sku: originalSku,
                    new_sku: itemData.sku,
                    product_name: editingItem.name,
                    bulk: false,
                    affected_count: 1
                  }
                });
              } else {
                const { data: updatedSiblings } = await supabase
                  .from('products')
                  .update({ sku: itemData.sku, updated_at: new Date().toISOString() })
                  .eq('admin_id', adminId)
                  .eq('name', editingItem.name)
                  .neq('id', productId)
                  .select('id');

                const totalAffected = (updatedSiblings?.length || 0) + 1;

                await supabase.from('activity_logs').insert({
                  admin_id: adminId,
                  user_id: userId,
                  action_type: 'sku_changed',
                  entity_type: 'product',
                  entity_id: productId,
                  description: `Bulk SKU update for "${editingItem.name}": ${originalSku || 'none'} → ${itemData.sku} (${totalAffected} variants)`,
                  metadata: {
                    old_sku: originalSku,
                    new_sku: itemData.sku,
                    product_name: editingItem.name,
                    bulk: true,
                    affected_count: totalAffected
                  }
                });

                await refreshInventory();
              }
            } catch {
              // Activity log failure is non-critical
            }
          }
        }

        onClose();
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBinAssignmentError(null);

    if (!formData.name.trim() || !formData.category.trim()) {
      alert(t('common.required'));
      return;
    }

    const skuChanged = isAdmin && editingItem && skuValue.trim() && skuValue.trim() !== originalSku;

    if (skuChanged) {
      if (skuConflict) return;
      if (skuValidating) return;
    }

    const primaryLocation = locationQuantities.length > 0
      ? locationQuantities.reduce((max, entry) => entry.quantity > max.quantity ? entry : max).locationName
      : formData.location.trim() || undefined;

    const itemData: any = {
      name: formData.name.trim(),
      category: formData.category.trim(),
      quantity: totalQuantity,
      location: selectedBinDetails ? undefined : primaryLocation,
      batch: formData.batch.trim() || undefined,
      reference_code: formData.reference_code.trim() || undefined,
      buy_price: hasPricingAccess ? (parseFloat(formData.buy_price) || undefined) : undefined,
      sell_price: hasPricingAccess ? (parseFloat(formData.sell_price) || undefined) : undefined,
      danger_category: formData.danger_category.trim() || undefined,
      image_url: formData.image_url || undefined,
      color_id: formData.color_id || undefined,
      size_id: formData.size_id || undefined,
      weight: formData.weight ? parseFloat(formData.weight) : undefined,
      weight_unit: formData.weight ? formData.weight_unit : undefined,
      ...(skuChanged ? { sku: skuValue.trim() } : {})
    };

    if (skuChanged) {
      setIsSubmitting(true);
      try {
        const { data: siblings } = await supabase
          .from('products')
          .select('id, name, color_id, size_id')
          .eq('admin_id', adminId)
          .eq('name', editingItem.name)
          .neq('id', editingItem.id);

        if (siblings && siblings.length > 0) {
          const siblingsWithNames: SiblingVariant[] = siblings.map(s => ({
            id: s.id,
            name: s.name,
            color: colors.find(c => c.id === s.color_id)?.name,
            size: sizes.find(sz => sz.id === s.size_id)?.name
          }));
          setSiblingVariants(siblingsWithNames);
          setPendingItemData(itemData);
          setShowBatchModal(true);
          return;
        }
      } finally {
        setIsSubmitting(false);
      }
    }

    await executeSubmit(itemData, false);
  };

  const handleBarcodeScanned = (barcode: string) => {
    setFormData(prev => ({
      ...prev,
      batch: barcode,
      name: prev.name || `Item ${barcode}`
    }));
    setShowScanner(false);
    if (onBarcodeScanned) {
      onBarcodeScanned(barcode);
    }
  };

  const usedLocationIds = new Set(locationQuantities.map(e => e.locationId));
  const canAddMoreLocations = availableLocations.some(l => !usedLocationIds.has(l.id));

  const skuHasChanged = isAdmin && editingItem && skuValue.trim() !== originalSku;
  const canSubmit = !isSubmitting && !(skuHasChanged && (skuConflict || skuValidating));

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm overflow-y-auto h-full w-full z-50">
        <div className="relative top-8 mx-auto mb-8 max-w-2xl">
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl overflow-hidden border border-gray-200 dark:border-gray-700">
            <div className="bg-gradient-to-r from-emerald-500 to-teal-600 px-6 py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center">
                    <Package className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-white">
                      {editingItem ? t('inventory.editItem') : t('inventory.addNewItem')}
                    </h3>
                    <p className="text-emerald-100 text-sm">
                      {editingItem ? 'Update item details' : 'Add a new item to inventory'}
                    </p>
                  </div>
                </div>
                <button
                  onClick={onClose}
                  className="p-2 text-white/80 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              <ItemImageUpload
                value={formData.image_url}
                onChange={(url) => setFormData({ ...formData, image_url: url })}
                adminId={adminId}
              />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label={t('inventory.itemName') + ' *'}
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder={t('inventory.enterItemName')}
                  required
                />

                <CategoryAutocomplete
                  label={t('product.category') + ' *'}
                  value={formData.category}
                  onChange={(value) => setFormData({ ...formData, category: value })}
                  categories={categories}
                  placeholder={t('inventory.enterCategory')}
                  required
                />

                <div className="flex gap-2">
                  <div className="flex-1">
                    <Input
                      label={t('inventory.batchNumber')}
                      value={formData.batch}
                      onChange={(e) => setFormData({ ...formData, batch: e.target.value })}
                      placeholder={t('inventory.enterOrScan')}
                    />
                  </div>
                  <div className="flex items-end">
                    <button
                      type="button"
                      onClick={() => setShowScanner(true)}
                      className="p-2.5 text-gray-500 hover:text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-900/30 rounded-lg border border-gray-200 dark:border-gray-700 transition-colors"
                      title="Scan barcode"
                    >
                      <Camera className="h-5 w-5" />
                    </button>
                  </div>
                </div>

                <Input
                  label="Reference Code"
                  value={formData.reference_code}
                  onChange={(e) => setFormData({ ...formData, reference_code: e.target.value })}
                  placeholder="e.g., ABC123, REF-001"
                />

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Color
                  </label>
                  <select
                    value={formData.color_id}
                    onChange={(e) => setFormData({ ...formData, color_id: e.target.value })}
                    className="input"
                  >
                    <option value="">No Color</option>
                    {colors.map(color => (
                      <option key={color.id} value={color.id}>
                        {color.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Size
                  </label>
                  <select
                    value={formData.size_id}
                    onChange={(e) => setFormData({ ...formData, size_id: e.target.value })}
                    className="input"
                  >
                    <option value="">No Size</option>
                    {sizes.map(size => (
                      <option key={size.id} value={size.id}>
                        {size.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="flex gap-2">
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Weight
                    </label>
                    <input
                      type="number"
                      step="0.001"
                      min="0"
                      value={formData.weight}
                      onChange={(e) => setFormData({ ...formData, weight: e.target.value })}
                      placeholder="0.00"
                      className="input"
                    />
                  </div>
                  <div className="w-24">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Unit
                    </label>
                    <select
                      value={formData.weight_unit}
                      onChange={(e) => setFormData({ ...formData, weight_unit: e.target.value })}
                      className="input"
                    >
                      <option value="g">g</option>
                      <option value="kg">kg</option>
                    </select>
                  </div>
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('inventory.safetyCategory')}
                  </label>
                  <select
                    value={formData.danger_category}
                    onChange={(e) => setFormData({ ...formData, danger_category: e.target.value })}
                    className="input"
                  >
                    <option value="">{t('inventory.noSafetyCategory')}</option>
                    <option value="Flammable">{t('inventory.flammable')}</option>
                    <option value="Toxic">{t('inventory.toxic')}</option>
                    <option value="Corrosive">{t('inventory.corrosive')}</option>
                    <option value="Explosive">{t('inventory.explosive')}</option>
                    <option value="Oxidizing">{t('inventory.oxidizing')}</option>
                    <option value="Compressed Gas">{t('inventory.compressedGas')}</option>
                    <option value="Radioactive">{t('inventory.radioactive')}</option>
                    <option value="Biohazard">{t('inventory.biohazard')}</option>
                  </select>
                </div>
              </div>

              <div className="p-4 bg-gradient-to-br from-slate-50 to-gray-100 dark:from-gray-800 dark:to-gray-850 rounded-xl border border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <MapPin className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
                    <h4 className="font-medium text-gray-900 dark:text-gray-100">
                      Location Quantities
                    </h4>
                  </div>
                  <div className="flex items-center gap-2 px-3 py-1.5 bg-emerald-100 dark:bg-emerald-900/30 rounded-lg">
                    <Calculator className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
                    <span className="text-sm font-semibold text-emerald-700 dark:text-emerald-300">
                      Total: {totalQuantity}
                    </span>
                  </div>
                </div>

                <div className="space-y-3">
                  {locationQuantities.map((entry) => {
                    const Icon = getLocationIcon(entry.locationName);
                    return (
                      <div
                        key={entry.locationId}
                        className="flex items-center gap-3 p-3 bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700"
                      >
                        <div className="flex items-center gap-2 min-w-[140px]">
                          <Icon className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                          <select
                            value={entry.locationId}
                            onChange={(e) => changeLocation(entry.locationId, e.target.value)}
                            className="text-sm font-medium text-gray-700 dark:text-gray-300 bg-transparent border-none focus:ring-0 p-0 cursor-pointer"
                          >
                            <option value={entry.locationId}>{entry.locationName}</option>
                            {availableLocations
                              .filter(l => !usedLocationIds.has(l.id) || l.id === entry.locationId)
                              .map(l => (
                                <option key={l.id} value={l.id}>{l.name}</option>
                              ))}
                          </select>
                        </div>

                        <div className="flex-1 flex items-center justify-end gap-2">
                          <button
                            type="button"
                            onClick={() => handleQuantityChange(entry.locationId, -1)}
                            disabled={entry.quantity <= 0}
                            className="w-8 h-8 flex items-center justify-center rounded-lg bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                          >
                            <Minus className="h-4 w-4" />
                          </button>

                          <input
                            type="number"
                            value={entry.quantity}
                            onChange={(e) => handleQuantityInput(entry.locationId, e.target.value)}
                            className="w-20 text-center font-semibold text-gray-900 dark:text-gray-100 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg py-1.5 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                            min="0"
                          />

                          <button
                            type="button"
                            onClick={() => handleQuantityChange(entry.locationId, 1)}
                            className="w-8 h-8 flex items-center justify-center rounded-lg bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 hover:bg-emerald-200 dark:hover:bg-emerald-900/50 transition-colors"
                          >
                            <Plus className="h-4 w-4" />
                          </button>

                          {locationQuantities.length > 1 && (
                            <button
                              type="button"
                              onClick={() => removeLocationRow(entry.locationId)}
                              className="ml-2 p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                              title="Remove location"
                            >
                              <X className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}

                  {canAddMoreLocations && (
                    <button
                      type="button"
                      onClick={addLocationRow}
                      className="w-full flex items-center justify-center gap-2 py-2.5 text-sm font-medium text-emerald-600 dark:text-emerald-400 hover:text-emerald-700 dark:hover:text-emerald-300 border border-dashed border-emerald-300 dark:border-emerald-700 rounded-lg hover:bg-emerald-50 dark:hover:bg-emerald-900/20 transition-colors"
                    >
                      <Plus className="h-4 w-4" />
                      Add Location
                    </button>
                  )}
                </div>
              </div>

              <div className="p-4 bg-cyan-50 dark:bg-cyan-900/20 rounded-xl border border-cyan-200 dark:border-cyan-800">
                <div className="flex items-center gap-2 mb-3">
                  <Box className="h-5 w-5 text-cyan-600 dark:text-cyan-400" />
                  <h4 className="font-medium text-gray-900 dark:text-gray-100">
                    Warehouse Bin Location
                  </h4>
                  <span className="text-xs text-gray-500 dark:text-gray-400">(Optional)</span>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                  Assign this item to a specific bin in your warehouse layout
                </p>
                <LocationPicker
                  value={formData.bin_location}
                  onChange={(location, binDetails) => {
                    setFormData({ ...formData, bin_location: location });
                    setSelectedBinDetails(binDetails || null);
                    setBinAssignmentError(null);
                  }}
                  placeholder="Select warehouse bin location..."
                />
                {selectedBinDetails && totalQuantity === 0 && (
                  <div className="mt-2 flex items-center gap-2 text-xs text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 px-3 py-2 rounded-lg">
                    <AlertCircle className="h-4 w-4 flex-shrink-0" />
                    <span>
                      Warning: You are assigning this product to a bin with 0 quantity. Consider adding inventory before bin assignment.
                    </span>
                  </div>
                )}
                {existingBinLocation && selectedBinDetails && existingBinLocation.bin_id !== selectedBinDetails.binId && (
                  <div className="mt-2 flex items-center gap-2 text-xs text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 px-3 py-2 rounded-lg">
                    <AlertCircle className="h-4 w-4 flex-shrink-0" />
                    <span>
                      This will transfer the item from <strong>{existingBinLocation.bin_code}</strong> to{' '}
                      <strong>{selectedBinDetails.binCode}</strong> with full quantity ({totalQuantity} units).
                    </span>
                  </div>
                )}
                {binAssignmentError && (
                  <div className="mt-2 flex items-center gap-2 text-xs text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 px-3 py-2 rounded-lg">
                    <AlertCircle className="h-4 w-4 flex-shrink-0" />
                    <span>{binAssignmentError}</span>
                  </div>
                )}
              </div>

              {hasPricingAccess && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-xl border border-blue-200 dark:border-blue-800">
                  <Input
                    label={t('common.buyPrice') + ' (EUR)'}
                    type="number"
                    step="0.01"
                    value={formData.buy_price}
                    onChange={(e) => setFormData({ ...formData, buy_price: e.target.value })}
                    placeholder="0.00"
                    min="0"
                  />

                  <Input
                    label={t('common.sellPrice') + ' (EUR)'}
                    type="number"
                    step="0.01"
                    value={formData.sell_price}
                    onChange={(e) => setFormData({ ...formData, sell_price: e.target.value })}
                    placeholder="0.00"
                    min="0"
                  />
                </div>
              )}

              {isAdmin && editingItem && (
                <div className="p-4 bg-amber-50 dark:bg-amber-900/10 rounded-xl border border-amber-200 dark:border-amber-800">
                  <div className="flex items-center gap-2 mb-1">
                    <Lock className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                    <h4 className="font-medium text-gray-900 dark:text-gray-100 text-sm">
                      Stock Keeping Unit (SKU)
                    </h4>
                    <span className="text-xs px-2 py-0.5 bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300 rounded-full font-medium">
                      Admin Only
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                    Changing the SKU will affect inventory tracking and any connected integrations. This action is logged.
                  </p>

                  {originalSku && (
                    <div className="flex items-center gap-2 mb-3">
                      <span className="text-xs text-gray-500 dark:text-gray-400">Current:</span>
                      <span className="font-mono text-xs px-2 py-1 bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 rounded-lg border border-gray-200 dark:border-gray-700">
                        {originalSku}
                      </span>
                    </div>
                  )}

                  <div className="flex gap-2">
                    <div className="flex-1 relative">
                      <div className="flex items-center">
                        <Tag className="absolute left-3 h-4 w-4 text-gray-400 pointer-events-none" />
                        <input
                          type="text"
                          value={skuValue}
                          onChange={(e) => setSkuValue(e.target.value.toUpperCase())}
                          placeholder="e.g., SKU250217-0001"
                          className={`input pl-9 pr-10 font-mono text-sm uppercase ${
                            skuConflict
                              ? 'border-red-400 dark:border-red-500 focus:ring-red-500 focus:border-red-500'
                              : skuAvailable
                              ? 'border-emerald-400 dark:border-emerald-500 focus:ring-emerald-500 focus:border-emerald-500'
                              : ''
                          }`}
                        />
                        <div className="absolute right-3 flex items-center">
                          {skuValidating && (
                            <Loader2 className="h-4 w-4 text-gray-400 animate-spin" />
                          )}
                          {!skuValidating && skuAvailable && (
                            <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                          )}
                          {!skuValidating && skuConflict && (
                            <AlertCircle className="h-4 w-4 text-red-500" />
                          )}
                        </div>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => setSkuValue(generateNewSKU())}
                      className="flex items-center gap-1.5 px-3 py-2 text-sm text-amber-700 dark:text-amber-300 bg-amber-100 dark:bg-amber-900/30 hover:bg-amber-200 dark:hover:bg-amber-900/50 rounded-lg border border-amber-200 dark:border-amber-700 transition-colors font-medium whitespace-nowrap"
                      title="Generate a new unique SKU"
                    >
                      <RefreshCw className="h-3.5 w-3.5" />
                      Regenerate
                    </button>
                  </div>

                  {skuConflict && (
                    <div className="mt-2 flex items-center gap-2 text-xs text-red-600 dark:text-red-400">
                      <AlertCircle className="h-3.5 w-3.5 flex-shrink-0" />
                      <span>Already in use by: <strong>{skuConflict.productName}</strong></span>
                    </div>
                  )}
                  {!skuValidating && skuAvailable && (
                    <div className="mt-2 flex items-center gap-2 text-xs text-emerald-600 dark:text-emerald-400">
                      <CheckCircle2 className="h-3.5 w-3.5 flex-shrink-0" />
                      <span>SKU is available</span>
                    </div>
                  )}
                </div>
              )}

              <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                <Button type="button" onClick={onClose} variant="secondary" disabled={isSubmitting}>
                  {t('action.cancel')}
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                  disabled={!canSubmit}
                  className="bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 border-0"
                >
                  {isSubmitting ? 'Saving...' : editingItem ? t('inventory.updateItem') : t('inventory.addItem')}
                </Button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {showBatchModal && pendingItemData && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[60] flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-11 h-11 rounded-xl bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center flex-shrink-0">
                  <Tag className="h-5 w-5 text-amber-600 dark:text-amber-400" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 dark:text-gray-100 text-lg leading-tight">
                    Apply SKU Change to Variants?
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Other variants of this product were found
                  </p>
                </div>
              </div>

              <div className="mb-4 p-3 bg-gray-50 dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">
                <div className="flex items-center gap-2 text-sm">
                  <span className="text-xs text-gray-500 dark:text-gray-400">From:</span>
                  <span className="font-mono text-xs px-2 py-1 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded">
                    {originalSku || 'none'}
                  </span>
                  <span className="text-gray-400 mx-1">→</span>
                  <span className="text-xs text-gray-500 dark:text-gray-400">To:</span>
                  <span className="font-mono text-xs px-2 py-1 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 rounded font-semibold">
                    {pendingItemData.sku}
                  </span>
                </div>
              </div>

              <div className="mb-5">
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                  <strong>{siblingVariants.length}</strong> other variant{siblingVariants.length !== 1 ? 's' : ''} of{' '}
                  <strong>"{editingItem?.name}"</strong> {siblingVariants.length !== 1 ? 'were' : 'was'} found:
                </p>
                <div className="space-y-1.5 max-h-36 overflow-y-auto pr-1">
                  {siblingVariants.map(sv => (
                    <div
                      key={sv.id}
                      className="flex items-center gap-2 text-xs px-3 py-2 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-100 dark:border-blue-800"
                    >
                      <Package className="h-3.5 w-3.5 text-blue-500 flex-shrink-0" />
                      <span className="text-gray-700 dark:text-gray-300">
                        {sv.color && <span className="font-medium">{sv.color}</span>}
                        {sv.color && sv.size && <span className="text-gray-400 mx-1">/</span>}
                        {sv.size && <span className="font-medium">{sv.size}</span>}
                        {!sv.color && !sv.size && <span className="text-gray-400 italic">No color / size</span>}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex flex-col gap-2">
                <button
                  type="button"
                  onClick={() => executeSubmit(pendingItemData, true)}
                  disabled={isSubmitting}
                  className="w-full py-2.5 px-4 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white rounded-xl font-medium text-sm transition-colors"
                >
                  {isSubmitting ? (
                    <span className="flex items-center justify-center gap-2">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Updating...
                    </span>
                  ) : (
                    `Update All ${siblingVariants.length + 1} Variants`
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => executeSubmit(pendingItemData, false)}
                  disabled={isSubmitting}
                  className="w-full py-2.5 px-4 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 disabled:opacity-50 text-gray-700 dark:text-gray-300 rounded-xl font-medium text-sm transition-colors"
                >
                  Update This Item Only
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowBatchModal(false);
                    setSiblingVariants([]);
                    setPendingItemData(null);
                  }}
                  disabled={isSubmitting}
                  className="w-full py-2 px-4 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 disabled:opacity-50 rounded-xl font-medium text-sm transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <BarcodeScanner
        isOpen={showScanner}
        onClose={() => setShowScanner(false)}
        onScan={handleBarcodeScanned}
        title={t('inventory.scanBarcodeTitle')}
      />
    </>
  );
};

export default AddItemForm;
